/**
 * Product System Entry Point
 * Handles product variant selection, pricing, and add to cart functionality
 */

// Import shared utilities (these will be resolved by the builder)
import { dom, events, format, validate } from '../shared/index.js';
import { VariantManager } from './VariantManager.js';
import '../components/MultiAddUpsell.js';

/**
 * Product System Class
 */
class ProductSystem {
  constructor(options = {}) {
    this.options = {
      productId: null,
      variantSelector: '[data-product-variant]',
      priceSelector: '[data-product-price]',
      addToCartSelector: '[data-add-to-cart]',
      quantitySelector: '[data-quantity]',
      ...options
    };
    
    this.product = null;
    this.selectedVariant = null;
    this.initialized = false;
    this.variantManager = null;
    
    this.init();
  }

  /**
   * Initialize the product system
   */
  async init() {
    try {
      await this.loadProductData();
      this.initializeVariantManager();
      this.bindEvents();
      this.updateUI();
      this.initialized = true;
      
      console.log('Product system initialized');
    } catch (error) {
      console.error('Failed to initialize product system:', error);
    }
  }

  /**
   * Load product data from the page or API
   */
  async loadProductData() {
    // Try to get product data from page
    const productScript = dom.$('script[data-product-json]');
    if (productScript) {
      try {
        this.product = JSON.parse(productScript.textContent);
        this.selectedVariant = this.product.variants?.[0] || null;
        return;
      } catch (error) {
        console.warn('Failed to parse product JSON from page');
      }
    }
    
    // Fallback: extract from current URL or form
    const productId = this.extractProductId();
    if (productId) {
      try {
        this.product = await this.fetchProductData(productId);
        this.selectedVariant = this.product.variants?.[0] || null;
      } catch (error) {
        console.warn('Product system: Could not fetch product data, continuing without product API', error);
        // Continue without product data - the page should still work
      }
    } else {
      console.log('Product system: No product ID found, skipping product API initialization');
    }
  }

  /**
   * Extract product ID from page
   */
  extractProductId() {
    // Try data attribute first
    const productEl = dom.$('[data-product-id]');
    if (productEl) {
      return productEl.dataset.productId;
    }
    
    // Try form input
    const productInput = dom.$('input[name="id"]');
    if (productInput) {
      return productInput.value;
    }
    
    return null;
  }

  /**
   * Fetch product data from API
   */
  async fetchProductData(productId) {
    try {
      // Validate product ID
      if (!productId || typeof productId !== 'string') {
        throw new Error(`Invalid product ID: ${productId}`);
      }
      
      const response = await fetch(`/products/${productId}.js`);
      if (!response.ok) {
        throw new Error(`Product API returned ${response.status} for product ID: ${productId}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch product data:', error);
      throw error;
    }
  }

  /**
   * Initialize the variant manager
   */
  initializeVariantManager() {
    console.log('🔍 [Product System] Checking for product form...');
    
    // Check if we're on a product page
    const productForm = document.querySelector('[data-product-form]');
    if (!productForm) {
      console.log('❌ [Product System] No product form found, skipping variant manager initialization');
      return;
    }

    console.log('✅ [Product System] Product form found, initializing variant manager...');

    // Wait for DOM to be fully ready
    if (document.readyState === 'loading') {
      console.log('⏳ [Product System] DOM still loading, waiting for DOMContentLoaded...');
      document.addEventListener('DOMContentLoaded', () => {
        console.log('📄 [Product System] DOMContentLoaded fired, creating variant manager...');
        this.createVariantManager();
      });
    } else {
      console.log('📄 [Product System] DOM already ready, creating variant manager...');
      this.createVariantManager();
    }
  }

  /**
   * Create the variant manager instance
   */
  createVariantManager() {
    console.log('🔧 [Product System] Creating VariantManager instance...');
    
    try {
      this.variantManager = new VariantManager({
        variantSelector: '[data-variant-select]',
        variantInput: '[data-variant-id]',
        priceSelector: '[data-product-price]',
        addToCartButton: '[data-submit-button]',
        buttonTextSelector: '[data-button-text]',
        urlParameter: 'variant'
      });

      // Listen for variant manager events
      this.bindVariantManagerEvents();
      
      console.log('✅ [Product System] VariantManager created successfully');
    } catch (error) {
      console.error('❌ [Product System] Failed to initialize variant manager:', error);
    }
  }

  /**
   * Bind variant manager events
   */
  bindVariantManagerEvents() {
    if (!this.variantManager) return;

    // Listen for variant change events
    document.addEventListener('variant:change', (event) => {
      const { variantId, variant, isAvailable, previousVariantId } = event.detail;
      
      // Update internal state
      this.selectedVariant = variant;
      
      // Emit legacy event for backward compatibility
      events.emit(document, 'product:variant-changed', {
        variant,
        product: this.product
      });
       
      console.log('Variant changed:', { 
        variantId, 
        variant: {
          id: variant.id,
          title: variant.title,
          available: variant.available,
          price: variant.price,
          compareAtPrice: variant.compare_at_price
        }, 
        isAvailable, 
        previousVariantId 
      });
    });

    // Listen for variant loaded events
    document.addEventListener('variant:loaded', (event) => {
      const { variantId, variant, isAvailable } = event.detail;
      
      // Update internal state
      this.selectedVariant = variant;
      
      console.log('Variant loaded from URL:', { 
        variantId, 
        variant: {
          id: variant.id,
          title: variant.title,
          available: variant.available,
          price: variant.price
        }, 
        isAvailable 
      });
    });

    // Listen for variant unavailable events
    document.addEventListener('variant:unavailable', (event) => {
      const { variantId, variant } = event.detail;
      
      console.log('Variant unavailable:', { variantId, variant });
      
      // Show error message
      this.showError('This variant is currently unavailable');
    });
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Variant selection
    const variantSelectors = dom.$$(this.options.variantSelector);
    variantSelectors.forEach(selector => {
      events.on(selector, 'change', this.handleVariantChange.bind(this));
    });

    // Quantity change
    const quantityInputs = dom.$$(this.options.quantitySelector);
    quantityInputs.forEach(input => {
      events.on(input, 'change', this.handleQuantityChange.bind(this));
    });

    // Add to cart
    const addToCartBtns = dom.$$(this.options.addToCartSelector);
    addToCartBtns.forEach(btn => {
      events.on(btn, 'click', this.handleAddToCart.bind(this));
    });
  }

  /**
   * Handle variant selection change
   */
  handleVariantChange(event) {
    const selectedOptions = this.getSelectedOptions();
    const variant = this.findVariantByOptions(selectedOptions);
    
    if (variant) {
      this.selectedVariant = variant;
      this.updateUI();
      
      // Emit custom event
      events.emit(document, 'product:variant-changed', {
        variant,
        product: this.product
      });
    }
  }

  /**
   * Get currently selected options
   */
  getSelectedOptions() {
    const options = [];
    const selectors = dom.$$(this.options.variantSelector);
    
    selectors.forEach(selector => {
      if (selector.type === 'radio' && selector.checked) {
        options.push(selector.value);
      } else if (selector.type === 'select-one') {
        options.push(selector.value);
      }
    });
    
    return options;
  }

  /**
   * Find variant by selected options
   */
  findVariantByOptions(selectedOptions) {
    if (!this.product?.variants) return null;
    
    return this.product.variants.find(variant => {
      return selectedOptions.every((option, index) => {
        const variantOption = variant[`option${index + 1}`];
        return variantOption === option;
      });
    });
  }

  /**
   * Handle quantity change
   */
  handleQuantityChange(event) {
    const quantity = parseInt(event.target.value, 10);
    
    if (quantity < 1) {
      event.target.value = 1;
      return;
    }
    
    // Update max quantity based on inventory
    if (this.selectedVariant?.inventory_quantity) {
      const maxQty = this.selectedVariant.inventory_quantity;
      if (quantity > maxQty) {
        event.target.value = maxQty;
      }
    }
  }

  /**
   * Handle add to cart
   */
  async handleAddToCart(event) {
    event.preventDefault();
    
    if (!this.selectedVariant) {
      this.showError('Please select a variant');
      return;
    }
    
    const quantity = this.getQuantity();
    const btn = event.target;
    
    try {
      btn.disabled = true;
      btn.textContent = 'Adding...';
      
      await this.addToCart(this.selectedVariant.id, quantity);
      
      // Emit success event
      events.emit(document, 'product:added-to-cart', {
        variant: this.selectedVariant,
        quantity,
        product: this.product
      });
      
    } catch (error) {
      this.showError('Failed to add to cart');
      console.error('Add to cart failed:', error);
    } finally {
      btn.disabled = false;
      btn.textContent = 'Add to Cart';
    }
  }

  /**
   * Get selected quantity
   */
  getQuantity() {
    const quantityInput = dom.$(this.options.quantitySelector);
    return quantityInput ? parseInt(quantityInput.value, 10) : 1;
  }

  /**
   * Add item to cart via API
   */
  async addToCart(variantId, quantity) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity
      })
    });
    
    if (!response.ok) {
      throw new Error(`Add to cart failed: ${response.status}`);
    }
    
    return await response.json();
  }

  /**
   * Update UI elements
   */
  updateUI() {
    this.updatePrice();
    this.updateAvailability();
    this.updateVariantInputs();
  }

  /**
   * Update price display
   */
  updatePrice() {
    const priceElements = dom.$$(this.options.priceSelector);
    
    if (this.selectedVariant && priceElements.length > 0) {
      const price = format.money(this.selectedVariant.price);
      const comparePrice = this.selectedVariant.compare_at_price;
      
      priceElements.forEach(el => {
        el.textContent = price;
        
        // Handle compare at price
        if (comparePrice && comparePrice > this.selectedVariant.price) {
          el.classList.add('on-sale');
          const comparePriceEl = el.querySelector('.compare-price');
          if (comparePriceEl) {
            comparePriceEl.textContent = format.money(comparePrice);
          }
        } else {
          el.classList.remove('on-sale');
        }
      });
    }
  }

  /**
   * Update availability status
   */
  updateAvailability() {
    const addToCartBtns = dom.$$(this.options.addToCartSelector);
    
    addToCartBtns.forEach(btn => {
      if (this.selectedVariant?.available) {
        btn.disabled = false;
        btn.textContent = 'Add to Cart';
      } else {
        btn.disabled = true;
        btn.textContent = 'Sold Out';
      }
    });
  }

  /**
   * Update variant form inputs
   */
  updateVariantInputs() {
    if (!this.selectedVariant) return;
    
    // Update hidden variant ID input
    const variantInput = dom.$('input[name="id"]');
    if (variantInput) {
      variantInput.value = this.selectedVariant.id;
    }
  }

  /**
   * Show error message
   */
  showError(message) {
    // Emit error event for notification system to handle
    events.emit(document, 'product:error', { message });
  }

  /**
   * Get current product data
   */
  getProduct() {
    return this.product;
  }

  /**
   * Get selected variant
   */
  getSelectedVariant() {
    return this.selectedVariant;
  }
}

// Auto-initialize if on product page
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (document.body.classList.contains('template-product')) {
      window.ProductSystem = new ProductSystem();
    }
  });
} else {
  if (document.body.classList.contains('template-product')) {
    window.ProductSystem = new ProductSystem();
  }
}

// Export for module usage
export default ProductSystem; 
